import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';

// Outer preview area (shows the full image, including what overflows the
// crop frame, dimmed) vs. the actual crop frame (bright square) — matches
// qrinvitation.com's "Crop Event Logo" tool.
const OUTER_SIZE = 350;
const FRAME_SIZE = 260;
const FRAME_X = (OUTER_SIZE - FRAME_SIZE) / 2;
const FRAME_Y = 30;

/**
 * Canvas-based logo cropper: the image can be dragged directly to pan (both
 * axes), a Zoom slider scales it, and a Height slider controls/reflects the
 * vertical pan position — both stay in sync whether you drag or use the
 * slider. The full image is shown bleeding outside the (dimmed) frame so
 * you can see what will be cropped away, just like the reference tool.
 */
const LogoCropModal = ({ open, imageSrc, onCancel, onConfirm }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open || !imageSrc) return;
    setZoom(1);
    setReady(false);
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const baseScale = Math.max(FRAME_SIZE / img.width, FRAME_SIZE / img.height);
      const drawW = img.width * baseScale;
      const drawH = img.height * baseScale;
      // Center the image within the crop frame initially
      setOffset({
        x: FRAME_X + (FRAME_SIZE - drawW) / 2,
        y: FRAME_Y + (FRAME_SIZE - drawH) / 2,
      });
      setReady(true);
    };
    img.src = imageSrc;
  }, [open, imageSrc]);

  const drawSize = useCallback(() => {
    const img = imgRef.current;
    if (!img) return { drawW: 0, drawH: 0 };
    const baseScale = Math.max(FRAME_SIZE / img.width, FRAME_SIZE / img.height);
    const scale = baseScale * zoom;
    return { drawW: img.width * scale, drawH: img.height * scale };
  }, [zoom]);

  // Keep the frame fully covered by the image at all times.
  const clamp = useCallback(
    (x, y) => {
      const { drawW, drawH } = drawSize();
      const minX = Math.min(FRAME_X, FRAME_X + FRAME_SIZE - drawW);
      const maxX = Math.max(FRAME_X, FRAME_X + FRAME_SIZE - drawW);
      const minY = Math.min(FRAME_Y, FRAME_Y + FRAME_SIZE - drawH);
      const maxY = Math.max(FRAME_Y, FRAME_Y + FRAME_SIZE - drawH);
      return {
        x: Math.min(maxX, Math.max(minX, x)),
        y: Math.min(maxY, Math.max(minY, y)),
      };
    },
    [drawSize]
  );

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, OUTER_SIZE, OUTER_SIZE);

    // Draw the full (possibly overflowing) image across the outer canvas.
    const { drawW, drawH } = drawSize();
    ctx.drawImage(img, offset.x, offset.y, drawW, drawH);

    // Dim everything outside the crop frame.
    ctx.fillStyle = 'rgba(107, 114, 128, 0.65)';
    ctx.fillRect(0, 0, OUTER_SIZE, FRAME_Y); // top
    ctx.fillRect(0, FRAME_Y + FRAME_SIZE, OUTER_SIZE, OUTER_SIZE - FRAME_Y - FRAME_SIZE); // bottom
    ctx.fillRect(0, FRAME_Y, FRAME_X, FRAME_SIZE); // left
    ctx.fillRect(FRAME_X + FRAME_SIZE, FRAME_Y, OUTER_SIZE - FRAME_X - FRAME_SIZE, FRAME_SIZE); // right

    // Frame border
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.strokeRect(FRAME_X, FRAME_Y, FRAME_SIZE, FRAME_SIZE);
  }, [drawSize, offset]);

  useEffect(() => {
    if (ready) draw();
  }, [ready, offset, zoom, draw]);

  useEffect(() => {
    if (!ready) return;
    setOffset((prev) => clamp(prev.x, prev.y));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, ready]);

  const getPoint = (e) => (e.touches ? e.touches[0] : e);

  const handlePointerDown = (e) => {
    if (!ready) return;
    const point = getPoint(e);
    dragRef.current = { startX: point.clientX, startY: point.clientY, offset };
    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('touchend', handlePointerUp);
  };

  const handlePointerMove = (e) => {
    if (!dragRef.current) return;
    e.preventDefault?.();
    const point = getPoint(e);
    const dx = point.clientX - dragRef.current.startX;
    const dy = point.clientY - dragRef.current.startY;
    setOffset(clamp(dragRef.current.offset.x + dx, dragRef.current.offset.y + dy));
  };

  const handlePointerUp = () => {
    dragRef.current = null;
    window.removeEventListener('mousemove', handlePointerMove);
    window.removeEventListener('mouseup', handlePointerUp);
    window.removeEventListener('touchmove', handlePointerMove);
    window.removeEventListener('touchend', handlePointerUp);
  };

  // Height slider: 0-100, mapped to the vertical pan range at current zoom.
  const verticalRange = useCallback(() => {
    const { drawH } = drawSize();
    const minY = Math.min(FRAME_Y, FRAME_Y + FRAME_SIZE - drawH);
    const maxY = Math.max(FRAME_Y, FRAME_Y + FRAME_SIZE - drawH);
    return { minY, maxY };
  }, [drawSize]);

  const { minY, maxY } = verticalRange();
  const heightPct = maxY === minY ? 50 : ((offset.y - minY) / (maxY - minY)) * 100;

  const handleHeightSlider = (val) => {
    const { minY, maxY } = verticalRange();
    const y = minY + (val / 100) * (maxY - minY);
    setOffset((prev) => clamp(prev.x, y));
  };

  const handleConfirm = () => {
    const img = imgRef.current;
    if (!img) return;
    // Export only the crop-frame region, at a clean square resolution.
    const OUT = 400;
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = OUT;
    exportCanvas.height = OUT;
    const ctx = exportCanvas.getContext('2d');
    const { drawW, drawH } = drawSize();
    const scaleOut = OUT / FRAME_SIZE;
    ctx.drawImage(
      img,
      (offset.x - FRAME_X) * scaleOut,
      (offset.y - FRAME_Y) * scaleOut,
      drawW * scaleOut,
      drawH * scaleOut
    );
    onConfirm(exportCanvas.toDataURL('image/png'));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Crop Event Logo</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4">
          <div
            style={{ width: OUTER_SIZE, height: OUTER_SIZE, cursor: ready ? 'grab' : 'default' }}
            className="select-none"
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
          >
            <canvas ref={canvasRef} width={OUTER_SIZE} height={OUTER_SIZE} />
          </div>

          <div className="w-full space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-sm w-14 text-gray-600">Height:</span>
              <input
                type="range"
                min="0"
                max="100"
                value={heightPct}
                onChange={(e) => handleHeightSlider(Number(e.target.value))}
                className="flex-1"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm w-14 text-gray-600">Zoom:</span>
              <input
                type="range"
                min="1"
                max="3"
                step="0.05"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1"
              />
            </div>
          </div>

          <Button
            className="w-full bg-orange-500 hover:bg-orange-600"
            onClick={handleConfirm}
            disabled={!ready}
          >
            Set Event Logo
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LogoCropModal;
